// src/sectors/pilates/sessions/components/calendar/CalendarTopBar.jsx
import React, { useMemo } from 'react';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import Icon from '@mui/material/Icon';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';

import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { CalendarToolsModal } from './modals/CalendarToolsModal';
import {
  IconButton,
  Menu,
  Divider,
  ListItemIcon,
  ListItemText,
  // optional: use if you want it
  // Chip,
} from '@mui/material';
import { useMe } from 'core/me/provider/MeProvider';

export function CalendarTopBar({
  weekLabel,
  toolsProps = null,
  onPrevWeek,
  onNextWeek,
  onToday,
  trainers = [],
  trainersLoading = false,
  onOpenWeekCopy = () => {},

  onEnterAttendance = () => {},
  attendanceMode = false,

  onEnterEdit = () => {},
  editMode = false,

  filterTrainerId = 'ALL',
  setFilterTrainerId = () => {},

  statusFilter = 'ALL',
  setStatusFilter = () => {},

  onOpenAttendanceList = () => {},

  lockEdit = false,
  lockAttendance = false,
}) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';
  const calType = toolsProps?.calendarType || 'TAM_SAAT';
  const { me } = useMe();
  const role = String(me?.membershipRole || ''); // "OWNER" | "ADMIN" | ...
  const canEditTools = role === 'OWNER' || role === 'ADMIN';
  // ✅ Yarım saat modlarında ALL yasak
  const requireTrainer = calType === 'YARIM_SAAT_YARIM';

  const isMdDown = useMediaQuery(theme.breakpoints.down('md')); // tablet+mobile
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm')); // phone
  const isXs = useMediaQuery('(max-width:420px)'); // extra tight phones
  const isMobile = isMdDown;

  const [toolsOpen, setToolsOpen] = React.useState(false);

  // ✅ mobile "more" menu
  const [moreEl, setMoreEl] = React.useState(null);
  const moreOpen = Boolean(moreEl);
  const openMore = (e) => setMoreEl(e.currentTarget);
  const closeMore = () => setMoreEl(null);

  // --------- tokens ----------
  const radius = isSmDown ? 12 : 14;
  const ctlH = isSmDown ? 34 : 38;

  const surface = isDark ? alpha('#fff', 0.06) : alpha('#0b1220', 0.04);
  const surfaceHover = isDark ? alpha('#fff', 0.09) : alpha('#0b1220', 0.06);
  const border = isDark ? alpha('#fff', 0.12) : alpha('#0b1220', 0.12);

  const topBg = isDark
    ? `linear-gradient(180deg, ${alpha('#fff', 0.06)} 0%, ${alpha('#fff', 0)} 75%)`
    : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
        '#fff',
        0
      )} 75%)`;

  const selectSx = {
    height: ctlH,
    borderRadius: `${radius}px`,
    background: surface,
    border: `1px solid ${border}`,
    '&:hover': { background: surfaceHover },
    '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.22)}` },
    '& .MuiOutlinedInput-notchedOutline': { border: '0 !important' },
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      gap: 0.6,
      py: 0,
      minHeight: 'unset',
      fontWeight: 900,
      fontSize: isSmDown ? 12.5 : 13,
      lineHeight: 1,
      paddingTop: 0,
      paddingBottom: 0,
      minWidth: 0,
    },
    '& .MuiSelect-icon': { right: 10 },
  };

  const navWrap = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.25,
    padding: 0.25,
    borderRadius: 999,
    border: `1px solid ${border}`,
    background: surface,
    boxShadow: isDark ? '0 6px 16px rgba(0,0,0,0.20)' : '0 6px 16px rgba(15,23,42,0.06)',
  };

  const navBtn = {
    height: ctlH,
    minWidth: ctlH,
    width: ctlH,
    borderRadius: `${radius}px`,
    border: 'none',
    background: 'transparent',
    boxShadow: 'none',
    color: theme.palette.text.primary,
    '&:hover': { background: isDark ? alpha('#fff', 0.08) : alpha('#0b1220', 0.06) },
    '&:active': { transform: 'translateY(1px)' },
    '&:focus-visible': {
      outline: 'none',
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  };

  const pillAction = (active, tone = 'primary', locked = false) => {
    const toneColor = tone === 'success' ? '#16a34a' : '#2563eb';
    const softBg = tone === 'success' ? 'rgba(22,163,74,0.14)' : 'rgba(37,99,235,0.14)';
    const softBorder = tone === 'success' ? 'rgba(22,163,74,0.35)' : 'rgba(37,99,235,0.35)';

    return {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.55,
      px: isXs ? 0.9 : 1.05,
      height: ctlH,
      borderRadius: '999px',
      fontWeight: 900,
      fontSize: isSmDown ? 12.5 : 13,
      userSelect: 'none',

      cursor: locked ? 'not-allowed' : 'pointer',
      opacity: locked ? 0.55 : 1,

      color: active ? '#fff' : toneColor,
      background: active
        ? `linear-gradient(135deg, ${toneColor}, ${tone === 'success' ? '#15803d' : '#1d4ed8'})`
        : softBg,
      border: `1.5px solid ${active ? 'rgba(0,0,0,.25)' : softBorder}`,
      boxShadow: active ? '0 10px 22px rgba(0,0,0,.28)' : '0 6px 14px rgba(0,0,0,.12)',
      transition: 'all 160ms ease',

      ...(locked
        ? {}
        : {
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: active ? '0 12px 26px rgba(0,0,0,.34)' : '0 8px 18px rgba(0,0,0,.18)',
            },
            '&:active': { transform: 'translateY(0)' },
          }),
    };
  };

  const actionBoxProps = (onClick, locked) => ({
    onClick: locked ? undefined : onClick,
    role: 'button',
    tabIndex: locked ? -1 : 0,
    onKeyDown: (e) => {
      if (locked) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.();
      }
    },
  });

  // labels
  const statusLabel =
    statusFilter === 'ALL'
      ? 'Tümü'
      : statusFilter === 'SUCCESS'
      ? 'Başarılı'
      : statusFilter === 'PENDING'
      ? 'Bekliyor'
      : 'İptal';

  const trainerLabel = (() => {
    const v = String(filterTrainerId ?? '');

    if (requireTrainer) {
      if (!v || v === 'ALL') return 'Eğitmen seçin';
      return trainers.find((t) => String(t.id) === v)?.name || `#${v}`;
    }

    if (v === 'ALL' || !v) return 'Tüm Eğitmenler';
    return trainers.find((t) => String(t.id) === v)?.name || `#${v}`;
  })();

  const attendanceText = isXs
    ? attendanceMode
      ? 'Kapat'
      : 'Yoklama'
    : attendanceMode
    ? 'Yoklamayı Kapat'
    : 'Yoklama Al';

  // ✅ İSTEK: mobilde "Kopyala" yerine her zaman "Hafta Kopyala" görünsün (kısaltma yok)
  const weekCopyText = 'Hafta Kopyala';

  // RIGHT controls wrap system (desktop behavior)
  const controlsWrapSx = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: isMdDown ? 'stretch' : 'flex-end',
    gap: 0.7,
    width: '100%',
    minWidth: 0,
  };

  const controlItemSx = {
    flex: isMdDown ? '1 1 160px' : '0 1 auto',
    minWidth: isXs ? 140 : 160,
    maxWidth: isMdDown ? '100%' : 280,
  };

  const safeTrainers = useMemo(
    () => (Array.isArray(trainers) ? trainers : []).filter((t) => String(t.id) !== 'ALL'),
    [trainers]
  );
  const trainerIds = useMemo(() => safeTrainers.map((t) => String(t.id)), [safeTrainers]);

  const safeTrainerValue = useMemo(() => {
    const v = String(filterTrainerId ?? '');

    if (requireTrainer && v === 'ALL') return '';
    if (v && trainerIds.includes(v)) return v;
    if (!requireTrainer && v === 'ALL') return 'ALL';
    return '';
  }, [filterTrainerId, requireTrainer, trainerIds]);

  React.useEffect(() => {
    if (!requireTrainer) return;
    if (String(filterTrainerId) !== 'ALL') return;

    const first = safeTrainers?.[0];
    if (first?.id != null) setFilterTrainerId(String(first.id));
  }, [requireTrainer, filterTrainerId, safeTrainers, setFilterTrainerId]);

  // ✅ Mobile: ultra-compact & symmetric layout
  const mobileRowGap = 0.6;

  const mobileWeekPillSx = {
    flex: '1 1 auto',
    minWidth: 0,
    px: 0.75,
    py: 0.45,
    borderRadius: '999px',
    border: `1px solid ${border}`,
    background: isDark ? alpha('#fff', 0.07) : alpha('#0b1220', 0.035),
    boxShadow: isDark ? '0 6px 16px rgba(0,0,0,0.25)' : '0 6px 16px rgba(15,23,42,0.06)',
  };

  const mobileGrid2 = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: mobileRowGap,
    width: '100%',
    minWidth: 0,
  };

  const moreBtnSx = {
    height: ctlH,
    width: ctlH,
    borderRadius: `${radius}px`,
    border: `1px solid ${border}`,
    background: surface,
    boxShadow: isDark ? '0 6px 16px rgba(0,0,0,0.20)' : '0 6px 16px rgba(15,23,42,0.06)',
    '&:hover': { background: surfaceHover },
    '&:active': { transform: 'translateY(1px)' },
  };

  // - Mobilde sağ aksiyon her zaman: editMode ? "Kapat" : "Düzenle"
  // - "Hafta Kopyala" HER ZAMAN (editMode açıkken) 3 nokta menüsünde durur
  const mobileRightAction = {
    label: editMode ? (isXs ? 'Kapat' : 'Düzenlemeyi Kapat') : 'Düzenle',
    icon: editMode ? 'check_circle' : 'edit',
    onClick: onEnterEdit, // toggle
    locked: lockEdit,
    tone: 'primary',
    active: editMode,
  };

  const MobileMoreMenu = () => (
    <Menu
      anchorEl={moreEl}
      open={moreOpen}
      onClose={closeMore}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          minWidth: 220,
          border: `1px solid ${border}`,
          background: isDark ? alpha('#0b1220', 0.92) : '#fff',
          backdropFilter: 'blur(10px)',
          boxShadow: isDark ? '0 18px 44px rgba(0,0,0,.45)' : '0 18px 44px rgba(15,23,42,.14)',
        },
      }}
    >
      {/* ✅ Hafta Kopyala: 3 noktada HER ZAMAN */}
      <MenuItem
        dense
        disabled={lockEdit || lockAttendance}
        onClick={() => {
          closeMore();
          if (lockEdit || lockAttendance) return;
          onOpenWeekCopy?.();
        }}
      >
        <ListItemIcon>
          <Icon fontSize="small">content_copy</Icon>
        </ListItemIcon>
        <ListItemText primary={weekCopyText} />
      </MenuItem>
      {attendanceMode ? (
        <MenuItem
          dense
          onClick={() => {
            closeMore();
            onOpenAttendanceList?.();
          }}
        >
          <ListItemIcon>
            <Icon fontSize="small">list_alt</Icon>
          </ListItemIcon>
          <ListItemText primary="Yoklama Listesi" />
        </MenuItem>
      ) : null}

      {(editMode || attendanceMode) && canEditTools ? <Divider sx={{ my: 0.5 }} /> : null}

      {canEditTools ? (
        <MenuItem
          dense
          onClick={() => {
            closeMore();
            setToolsOpen(true);
          }}
        >
          <ListItemIcon>
            <Icon fontSize="small">settings</Icon>
          </ListItemIcon>
          <ListItemText primary="Takvim Ayarları" />
        </MenuItem>
      ) : null}
    </Menu>
  );

  const MobileLayout = () => (
    <MDBox sx={{ display: 'flex', flexDirection: 'column', gap: mobileRowGap }}>
      {/* Row 1: Nav + Week + More (symmetric) */}
      <MDBox sx={{ display: 'flex', alignItems: 'center', gap: mobileRowGap, minWidth: 0 }}>
        <MDBox sx={{ ...navWrap, flex: '0 0 auto' }}>
          <Tooltip arrow enterDelay={300} title="Önceki">
            <Button onClick={onPrevWeek} size="small" sx={navBtn}>
              <Icon fontSize="small">chevron_left</Icon>
            </Button>
          </Tooltip>
          <Tooltip arrow enterDelay={300} title="Bugün">
            <Button onClick={onToday} size="small" sx={navBtn}>
              <Icon fontSize="small">today</Icon>
            </Button>
          </Tooltip>
          <Tooltip arrow enterDelay={300} title="Sonraki">
            <Button onClick={onNextWeek} size="small" sx={navBtn}>
              <Icon fontSize="small">chevron_right</Icon>
            </Button>
          </Tooltip>
        </MDBox>

        <MDBox sx={mobileWeekPillSx}>
          <MDTypography
            variant="body2"
            fontWeight="bold"
            sx={{
              fontSize: 12.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={weekLabel || 'Bu hafta'}
          >
            {weekLabel || 'Bu hafta'}
          </MDTypography>
        </MDBox>

        <IconButton onClick={openMore} size="small" sx={moreBtnSx}>
          <Icon fontSize="small">more_vert</Icon>
        </IconButton>
        <MobileMoreMenu />
      </MDBox>

      {/* Row 2: Filters (2-col grid, symmetric) */}
      <MDBox sx={mobileGrid2}>
        <FormControl size="small" sx={{ width: '100%' }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={selectSx}
            displayEmpty
            renderValue={() => (
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {statusLabel}
              </span>
            )}
          >
            <MenuItem value="ALL">Tümü</MenuItem>
            <MenuItem value="SUCCESS">Başarılı</MenuItem>
            <MenuItem value="PENDING">Bekliyor</MenuItem>
            <MenuItem value="CANCELLED">İptal</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ width: '100%' }}>
          <Select
            value={safeTrainerValue}
            onChange={(e) => setFilterTrainerId(e.target.value)}
            sx={selectSx}
            displayEmpty
            disabled={trainersLoading}
            renderValue={() => (
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {trainerLabel}
              </span>
            )}
          >
            <MenuItem value="">
              <em>Eğitmen seçin</em>
            </MenuItem>

            {!requireTrainer ? <MenuItem value="ALL">Tüm Eğitmenler</MenuItem> : null}

            {safeTrainers.map((t) => (
              <MenuItem key={t.id} value={String(t.id)}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </MDBox>

      {/* Row 3: Actions (2-col grid, symmetric) */}
      <MDBox sx={mobileGrid2}>
        <MDBox
          {...actionBoxProps(onEnterAttendance, lockAttendance)}
          sx={{
            ...pillAction(attendanceMode, 'success', lockAttendance),
            width: '100%',
          }}
        >
          <Icon sx={{ fontSize: 18 }}>{attendanceMode ? 'how_to_reg' : 'fact_check'}</Icon>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {attendanceText}
          </span>
        </MDBox>

        <MDBox
          {...actionBoxProps(mobileRightAction.onClick, mobileRightAction.locked)}
          sx={{
            ...pillAction(
              mobileRightAction.active,
              mobileRightAction.tone,
              mobileRightAction.locked
            ),
            width: '100%',
          }}
        >
          <Icon sx={{ fontSize: 18 }}>{mobileRightAction.icon}</Icon>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {mobileRightAction.label}
          </span>
        </MDBox>
      </MDBox>
    </MDBox>
  );

  return (
    <MDBox
      sx={{
        px: isSmDown ? 0.9 : 1.25,
        py: isSmDown ? 0.8 : 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
        background: topBg,
        backdropFilter: 'saturate(140%) blur(8px)',
        boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.22)' : '0 10px 26px rgba(15,23,42,0.08)',
      }}
    >
      {/* ✅ Mobile: minimal + symmetric (desktop görünüm değişmesin) */}
      {isMobile ? (
        <MobileLayout />
      ) : (
        <MDBox
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(220px, 1fr) auto minmax(360px, 1fr)',
            gap: 1,
            alignItems: 'center',
          }}
        >
          {/* LEFT: title */}
          <MDBox
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 1,
              minWidth: 0,
            }}
          >
            <MDBox sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
              <MDTypography
                variant="button"
                fontWeight="bold"
                sx={{
                  whiteSpace: 'nowrap',
                  letterSpacing: 0.2,
                  fontSize: 14,
                }}
              >
                Seans Takvimi
              </MDTypography>

              <MDTypography
                variant="caption"
                sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap', opacity: 0.85 }}
              >
                Haftalık
              </MDTypography>
            </MDBox>
          </MDBox>

          {/* CENTER: nav + week label */}
          <MDBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.9,
              minWidth: 0,
            }}
          >
            <MDBox sx={navWrap}>
              <Tooltip arrow enterDelay={300} title="Önceki">
                <Button onClick={onPrevWeek} size="small" sx={navBtn}>
                  <Icon fontSize="small">chevron_left</Icon>
                </Button>
              </Tooltip>

              <Tooltip arrow enterDelay={300} title="Bugün">
                <Button onClick={onToday} size="small" sx={navBtn}>
                  <Icon fontSize="small">today</Icon>
                </Button>
              </Tooltip>

              <Tooltip arrow enterDelay={300} title="Sonraki">
                <Button onClick={onNextWeek} size="small" sx={navBtn}>
                  <Icon fontSize="small">chevron_right</Icon>
                </Button>
              </Tooltip>
            </MDBox>

            <MDBox
              sx={{
                px: 1.0,
                py: 0.55,
                borderRadius: '999px',
                border: `1px solid ${border}`,
                background: isDark ? alpha('#fff', 0.07) : alpha('#0b1220', 0.035),
                boxShadow: isDark
                  ? '0 6px 16px rgba(0,0,0,0.25)'
                  : '0 6px 16px rgba(15,23,42,0.06)',
                maxWidth: 360,
                minWidth: 0,
              }}
            >
              <MDTypography
                variant="body2"
                fontWeight="bold"
                sx={{
                  fontSize: 13,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={weekLabel || 'Bu hafta'}
              >
                {weekLabel || 'Bu hafta'}
              </MDTypography>
            </MDBox>
          </MDBox>

          {/* RIGHT: controls (desktop aynı) */}
          <MDBox sx={controlsWrapSx}>
            {/* STATUS */}
            <MDBox sx={controlItemSx}>
              <FormControl size="small" sx={{ width: '100%' }}>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={selectSx}
                  displayEmpty
                  renderValue={() => (
                    <span
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}
                    >
                      <Icon fontSize="small">filter_alt</Icon>
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {statusLabel}
                      </span>
                    </span>
                  )}
                >
                  <MenuItem value="ALL">Tümü</MenuItem>
                  <MenuItem value="SUCCESS">Başarılı</MenuItem>
                  <MenuItem value="PENDING">Bekliyor</MenuItem>
                  <MenuItem value="CANCELLED">İptal</MenuItem>
                </Select>
              </FormControl>
            </MDBox>

            {/* TRAINER */}
            <MDBox sx={controlItemSx}>
              <FormControl size="small" sx={{ width: '100%' }}>
                <Select
                  value={safeTrainerValue}
                  onChange={(e) => setFilterTrainerId(e.target.value)}
                  sx={selectSx}
                  displayEmpty
                  disabled={trainersLoading}
                  renderValue={() => (
                    <span
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}
                    >
                      <Icon fontSize="small">sports</Icon>
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {trainerLabel}
                      </span>
                    </span>
                  )}
                >
                  <MenuItem value="">
                    <em>Eğitmen seçin</em>
                  </MenuItem>

                  {!requireTrainer ? <MenuItem value="ALL">Tüm Eğitmenler</MenuItem> : null}

                  {safeTrainers.map((t) => (
                    <MenuItem key={t.id} value={String(t.id)}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MDBox>

            {/* SETTINGS (desktop only here) */}
            {canEditTools ? (
              <MDBox sx={{ flex: '0 0 auto' }}>
                <Tooltip arrow enterDelay={300} title="Takvim Ayarları">
                  <IconButton
                    onClick={() => setToolsOpen(true)}
                    size="small"
                    sx={{
                      height: ctlH,
                      width: ctlH,
                      borderRadius: `${radius}px`,
                      border: `1px solid ${border}`,
                      background: surface,
                      boxShadow: isDark
                        ? '0 6px 16px rgba(0,0,0,0.20)'
                        : '0 6px 16px rgba(15,23,42,0.06)',
                      '&:hover': { background: surfaceHover },
                      '&:active': { transform: 'translateY(1px)' },
                    }}
                  >
                    <Icon fontSize="small">settings</Icon>
                  </IconButton>
                </Tooltip>
              </MDBox>
            ) : null}

            {/* ATTENDANCE */}
            <MDBox
              {...actionBoxProps(onEnterAttendance, lockAttendance)}
              sx={{
                ...pillAction(attendanceMode, 'success', lockAttendance),
                flex: '0 0 auto',
                minWidth: 160,
              }}
            >
              <Icon sx={{ fontSize: 18 }}>{attendanceMode ? 'how_to_reg' : 'fact_check'}</Icon>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {attendanceMode ? 'Yoklamayı Kapat' : 'Yoklama Al'}
              </span>
            </MDBox>

            {/* Attendance list (only when open) */}
            {attendanceMode ? (
              <MDBox
                {...actionBoxProps(onOpenAttendanceList, false)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.6,
                  px: 1.15,
                  height: ctlH,
                  borderRadius: '999px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  background: 'rgba(245, 158, 11, 0.16)',
                  border: '1px solid rgba(245, 158, 11, 0.38)',
                  color: 'rgb(180, 83, 9)',
                  fontWeight: 900,
                  fontSize: 13,
                  transition: 'all 140ms ease',
                  '&:hover': {
                    background: 'rgba(245, 158, 11, 0.24)',
                    borderColor: 'rgba(245, 158, 11, 0.55)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': { transform: 'translateY(0)' },
                  flex: '0 0 auto',
                  minWidth: 160,
                }}
              >
                <Icon sx={{ fontSize: 17 }}>list_alt</Icon>
                <span
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  Yoklama Listesi
                </span>
              </MDBox>
            ) : null}

            {/* WEEK COPY (only in edit mode) */}
            {editMode ? (
              <MDBox
                {...actionBoxProps(onOpenWeekCopy, lockEdit)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.6,
                  px: 1.2,
                  height: ctlH,
                  borderRadius: '999px',
                  userSelect: 'none',
                  cursor: lockEdit ? 'not-allowed' : 'pointer',
                  opacity: lockEdit ? 0.55 : 1,
                  background: 'rgba(245, 158, 11, 0.16)',
                  border: '1px solid rgba(245, 158, 11, 0.40)',
                  color: 'rgb(180, 83, 9)',
                  fontWeight: 900,
                  fontSize: 13,
                  transition: 'all 140ms ease',
                  '&:hover':
                    lockEdit || lockAttendance
                      ? undefined
                      : {
                          background: 'rgba(245, 158, 11, 0.24)',
                          borderColor: 'rgba(245, 158, 11, 0.55)',
                          transform: 'translateY(-1px)',
                        },
                  '&:active':
                    lockEdit || lockAttendance ? undefined : { transform: 'translateY(0)' },
                  flex: '0 0 auto',
                  minWidth: 160,
                }}
              >
                <Icon sx={{ fontSize: 17 }}>content_copy</Icon>
                <span
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  Hafta Kopyala
                </span>
              </MDBox>
            ) : null}

            {/* EDIT */}
            <MDBox
              {...actionBoxProps(onEnterEdit, lockEdit)}
              sx={{
                ...pillAction(editMode, 'primary', lockEdit),
                flex: '0 0 auto',
                minWidth: 160,
              }}
            >
              <Icon sx={{ fontSize: 18 }}>{editMode ? 'check_circle' : 'edit'}</Icon>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {editMode ? 'Bitir' : 'Düzenle'}
              </span>
            </MDBox>
          </MDBox>
        </MDBox>
      )}

      {canEditTools ? (
        <CalendarToolsModal
          open={toolsOpen}
          onClose={() => setToolsOpen(false)}
          onSaved={() => {
            setToolsOpen(false);
            window.location.reload();
          }}
        />
      ) : null}
    </MDBox>
  );
}
